import { useTranslation } from 'react-i18next';

import { Stack, Typography } from '@mui/material';

import { ReactComponent as ChevronDownIcon } from '@/assets/icons/ChevronDownRounded.svg';
import { ReactComponent as ChevronUpIcon } from '@/assets/icons/ChevronUpRounded.svg';
import { useModuleChipColors } from '@/shared/lib/hooks/useModuleChipColors';
import CustomCheckbox from '@/shared/ui/CustomCheckbox';

import GeneratikTableGroupTotalPill from './GeneratikTableGroupTotalPill';
import {
  GroupHeaderContainer,
  GroupHeaderWrapper,
  SelectedCountChip,
  ToggleIcon,
  checkboxBoxSx,
  chipTextSx,
  labelSx,
} from './GeneratikTableListViewGroupHeader.styles';

interface GeneratikTableListViewGroupHeaderProps {
  label: string;
  totalCount: number;
  selectedCount: number;
  selectedPrice?: number;
  totalPrice?: number;
  currency?: string;
  priceForPeriod?: boolean;
  expanded: boolean;
  onToggle: () => void;
  onToggleGroupSelection?: () => void;
  checkboxDisabled?: boolean;
}

const GeneratikTableListViewGroupHeader = ({
  label,
  totalCount,
  selectedCount,
  selectedPrice,
  totalPrice,
  currency = '€',
  priceForPeriod,
  expanded,
  onToggle,
  onToggleGroupSelection,
  checkboxDisabled,
}: GeneratikTableListViewGroupHeaderProps) => {
  const { t } = useTranslation();
  const { text: chipColor } = useModuleChipColors();
  const isAllSelected = selectedCount === totalCount;
  const isPartial = selectedCount > 0 && selectedCount < totalCount;

  return (
    <GroupHeaderWrapper expanded={expanded}>
      <GroupHeaderContainer onClick={onToggle}>
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <Stack sx={checkboxBoxSx} onClick={(e) => e.stopPropagation()}>
            <CustomCheckbox
              checked={isAllSelected}
              indeterminate={isPartial}
              onChange={() => onToggleGroupSelection?.()}
              disabled={checkboxDisabled}
            />
          </Stack>
          <ToggleIcon>{expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}</ToggleIcon>
          <Typography color="text.secondary" sx={labelSx}>
            {label}
          </Typography>
          <Typography color="text.secondary" sx={labelSx}>
            ({totalCount})
          </Typography>
          {selectedCount > 0 && (
            <SelectedCountChip chipColor={chipColor}>
              <Typography sx={chipTextSx}>{selectedCount}</Typography>
            </SelectedCountChip>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" gap="0.5rem">
          {selectedPrice !== undefined && (
            <GeneratikTableGroupTotalPill
              label={t('Selected')}
              price={selectedPrice}
              currency={currency}
              priceForPeriod={priceForPeriod}
              testId="group-selected-price"
            />
          )}
          {totalPrice !== undefined && (
            <GeneratikTableGroupTotalPill
              label={t('Total')}
              price={totalPrice}
              currency={currency}
              priceForPeriod={priceForPeriod}
            />
          )}
        </Stack>
      </GroupHeaderContainer>
    </GroupHeaderWrapper>
  );
};

export default GeneratikTableListViewGroupHeader;
